import React, {useEffect} from "react";
import {z} from "zod";
import LoadingButton from '@mui/lab/LoadingButton';

import InputContainer from "@/components/form/InputContainer.jsx";
import Input from '@/components/form/MUIInput.jsx';
import Form from "@/components/form/Form.jsx";
import {useForm} from "@/hooks/useForm.jsx";
import DividingLine from "@/components/form/DividingLine.jsx";
import {useFormProgress} from "@/hooks/useFormProgress.jsx";
import ContainerBox from "@/components/ContainerBox.jsx";
import {ContainerBoxHeader, ContainerBoxTitle} from "@/components/typography/index.jsx";
import PasswordInput from "@/components/form/MUIPasswordInput.jsx";
import {STUDENT_EDUCATIONAL_BACKGROUND} from "@/constants/routes/sign-up.jsx";
import {SIGN_UP_PERSONAL_INFORMATION_ENDPOINT} from "@/constants/endpoints.jsx";
import ResumeUpload from "@/components/form/ResumeUpload.jsx";
import {usePreFetchData} from "@/hooks/usePreFetchData.jsx";
import {urlToFile} from "@/utils/urlToFile.jsx";
import {useLocalStorage} from "@/hooks/useLocalStorage.jsx";
import {toBase64} from "@/utils/toBase64.jsx";
import {base64ToFile} from "@/utils/base64ToFile.jsx";
import {setGTMUserData} from "@/utils/setGTMUserData.jsx";

const schema = z.object({
    first_name: z.string().min(1, "Your First Name is required"),
    last_name: z.string().min(1, "Your Last Name is required"),
    resume: z.union([z.instanceof(File), z.null()]).optional(),
    email: z.string().min(1, "Email is required"),
    password: z.string().min(1, "Password is required"),
});

export default function StudentInformation({navigateOnSubmitTo = STUDENT_EDUCATIONAL_BACKGROUND}) {
    const {navigate, setCurrentProgress} = useFormProgress();
    const {preFetchedData, reFetchPreData} = usePreFetchData();
    const {storeData, getStoredData} = useLocalStorage();

    const {
        register,
        onSubmit,
        getValues,
        getFieldState,
        state,
        isValid,
        isLoading,
        clearErrors,
        setValue
    } = useForm({
        endpoint: SIGN_UP_PERSONAL_INFORMATION_ENDPOINT,
        schema,
        defaultValues: {
            first_name: '',
            last_name: '',
            resume: null,
            email: '',
            password: '',
        }
    });

    useEffect(() => {
        setCurrentProgress(1);
    }, []);

    useEffect(() => {
        if(preFetchedData?.user_id) {
            setValue('first_name', preFetchedData.first_name);
            setValue('last_name', preFetchedData.last_name);
            if (preFetchedData?.resume?.length > 0) {
                urlToFile(preFetchedData.resume).then(file => setValue('resume', file));
            }
            setValue('email', preFetchedData.email);
            setValue('password', '******');
        }

        const storedData = getStoredData();

        if(storedData) {
            setValue('first_name', storedData.first_name);
            setValue('last_name', storedData.last_name);
            if(!preFetchedData?.user_id) {
                setValue('email', storedData.email);

                if (storedData.resume?.length > 0) {
                    base64ToFile(storedData.resume, 'resume.pdf').then(file => setValue('resume', file));
                }
            }
        }
    }, [preFetchedData, getStoredData]);

    const handleSubmit = (endpointResponse, data) => {
        setGTMUserData(endpointResponse?.data?.userData);

        reFetchPreData();

        if(endpointResponse?.data?.redirect) {
            window.location = endpointResponse.data.redirect;
        } else {
            navigate(navigateOnSubmitTo);
        }
    }

    return (
        <>
            <ContainerBox>
                <ContainerBoxHeader>
                    <ContainerBoxTitle>Personal Information</ContainerBoxTitle>
                </ContainerBoxHeader>

                <Form onSubmit={onSubmit(handleSubmit)}>
                    <InputContainer>
                        <Input
                            label={"Your First Name"}
                            placeholder={"First Name"}
                            size={'xsm'}
                            {...register("first_name")}
                            errorMessage={state?.errors?.first_name?.message ?? ''}
                        />

                        <Input
                            label={"Your Last Name"}
                            placeholder={"Last Name"}
                            size={'xsm'}
                            {...register("last_name")}
                            errorMessage={state?.errors?.last_name?.message ?? ''}
                        />
                    </InputContainer>

                    <InputContainer>
                        <ResumeUpload
                            file={getValues('resume')}
                            error={getFieldState('resume')?.error?.message ?? null}
                            onChange={(file) => {
                                clearErrors('resume');

                                if (!file) {
                                    setValue('resume', null);
                                    storeData('resume', null);
                                } else {
                                    setValue('resume', file, {shouldValidate: true});
                                    toBase64(file).then((resume) => {
                                        storeData('resume', resume);
                                    });
                                }
                            }}
                        />
                    </InputContainer>

                    <DividingLine/>

                    <InputContainer>
                        <Input
                            label={"Email Address"}
                            placeholder={"Email Address"}
                            size={'md'}
                            {...register("email")}
                            errorMessage={state?.errors?.email?.message ?? ''}
                            disabled={preFetchedData?.user_id}
                        />
                    </InputContainer>

                    <InputContainer>
                        <PasswordInput
                            label={"Create a Password"}
                            placeholder={"Create a Password"}
                            size={'md'}
                            {...register("password")}
                            errorMessage={state?.errors?.password?.message ?? ''}
                            disabled={preFetchedData?.user_id}
                        />
                    </InputContainer>

                    <LoadingButton
                        loading={isLoading}
                        disabled={!isValid}
                        variant="contained"
                        type="submit"
                        size="medium"
                    >
                        Continue →
                    </LoadingButton>
                </Form>
            </ContainerBox>
        </>
    );
};
